import Hls from 'hls.js';
import {
  FRAG_SAMPLE_INTERVAL,
  BUFFER_LOW_THRESHOLD,
  LL_BUFFER_LOW_THRESHOLD,
  BUFFER_CRITICAL_THRESHOLD,
  BUFFER_RECOVERY_THRESHOLD,
  FRAG_LOAD_ERROR_THRESHOLD,
  FRAG_GAP_ERROR_THRESHOLD,
  QUALITY_REASSERT_HOLD_MS,
} from './constants';
import { accumulateSpeedSample } from './speedMonitor';
import { readResponseServer } from './responseServer';
import { buildRequestMetrics, requestKindForError } from './requestMetrics';
import {
  alignInitialLiveLatency,
  applyLatencyControl,
  bufferRecoveryThreshold,
  shouldSuspendForBuffer,
  slowDownThreshold,
  type AdaptiveLatencyTarget,
  type LagInstabilityDetector,
  type CorrectionState,
} from './latencyController';

export type BaseHlsEngineBindings = {
  hls: Hls | null;
  video: HTMLVideoElement | null;
  eventBus: any;
  store: any;
  logger: any;
  ctx: {
    autoplay: boolean;
    onManifestReady?: () => void;
    isBackgroundPlayback?: () => boolean;
    suppressLoadingState?: () => boolean;
    onInitialLiveLatencyAligned?: () => void;
  };
  lastSource: { master?: string; source?: string } | null;
  isLowLatency: boolean;
  shouldResumeOnNextManifest: boolean;
  hlsLoadStarted: boolean;
  isLevelSwitching: boolean;
  levelSwitchStartedAt: number;
  fragLoadErrorCount: number;
  fragGapErrorCount: number;
  isLatencyPausedForBuffer: boolean;
  pendingQualityReassert: boolean;
  lastQualityReassertAt: number;
  speedDownloadSpeeds: number[];
  speedTotalBytes: number;
  speedTotalTime: number;
  lastFragSampleAt: number;
  restartCount: number;
  getQualities: () => any[];
  setQuality: (quality: any) => void;
  play: () => void;
  getBufferLength: () => number;
  getBandwidth: () => number;
  applyQualityLevel: (idx: number) => void;
  extractResponseCode: (data: unknown) => number | null;
  onFragLoadErrorDuringSwitch: (fragStart: number) => void;
  onFragLoadErrorThreshold: () => void;
  isAccessDeniedError: (data: unknown, responseCode: number | null) => boolean;
  stop: () => void;
  scheduleRestart: (delay: number) => void;
  startSpeedMonitor: () => void;
  lastLatencyRateAt: number;
  instabilityDetector?: LagInstabilityDetector;
  latencyTarget: AdaptiveLatencyTarget;
  latencyCorrectionState: CorrectionState;
  initialLiveLatencyAligned: boolean;
  initialLiveLatencySeeked: boolean;
  hasInitialLiveLatencyRevealBuffer: () => boolean;
  completeInitialLiveLatencyAlignment: () => void;
};

export function bindHlsEventsModule(engine: BaseHlsEngineBindings): void {
  const hls = engine.hls!;
  const fragmentServers = new WeakMap<object, ReturnType<typeof readResponseServer>>();
  let previousLevelHeight: number | undefined;
  for (const event of [Hls.Events.AUDIO_TRACK_LOADED, Hls.Events.SUBTITLE_TRACK_LOADED]) {
    if (typeof event !== 'string') continue;
    hls.on(event, (_event: unknown, data: any) => {
      engine.eventBus.emit('hls:request', buildRequestMetrics('playlist', data));
    });
  }

  hls.on(Hls.Events.MANIFEST_LOADED, (_event: unknown, data: any) => {
    engine.eventBus.emit('hls:request', buildRequestMetrics('manifest', data));
    engine.eventBus.emit('hls:playlist:loaded');
    const server = readResponseServer(data?.networkDetails);
    engine.store.setState({ cdnServer: server.name, cdnServerIp: server.ip, cdnServerSource: 'manifest' });
  });

  hls.on(Hls.Events.MANIFEST_PARSED, (_event: unknown, data: any) => {
    engine.ctx.onManifestReady?.();
    const qualities = engine.getQualities();
    engine.store.setState({ availableQualities: qualities });
    engine.eventBus.emit('quality:available', qualities);
    engine.eventBus.emit('engine:ready');
    engine.instabilityDetector?.reset();
    engine.isLatencyPausedForBuffer = false;
    engine.logger.info(`Manifest parsed: ${data.levels.length} levels`);

    const shouldResume = engine.shouldResumeOnNextManifest;
    const shouldPlay = engine.ctx.autoplay === true || shouldResume;

    const preloadPausedPreview = (): void => {
      const PREVIEW_PRELOAD_MAX_FRAGS = 3;
      // Safety net: if FRAG_BUFFERED and loadeddata never fire (e.g. network
      // stall at startup), force-stop the preview load after this deadline so
      // the engine is not left streaming indefinitely with autoplay=false.
      const PREVIEW_SAFETY_TIMEOUT_MS = 2_000;

      hls.startLoad();
      engine.hlsLoadStarted = true;

      let preloadStopped = false;
      let bufferedFragCount = 0;
      let safetyTimer: ReturnType<typeof setTimeout> | null = null;

      const onFragBuffered = (): void => {
        bufferedFragCount++;
        maybeStopPreload();
      };

      const onLoadedData = (): void => {
        maybeStopPreload();
      };

      const cleanupPreloadListeners = (): void => {
        if (safetyTimer !== null) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        hls.off(Hls.Events.FRAG_BUFFERED, onFragBuffered);
        engine.video?.removeEventListener('loadeddata', onLoadedData);
      };

      const stopPreload = (): void => {
        if (preloadStopped) return;
        preloadStopped = true;

        cleanupPreloadListeners();

        const isActuallyPlaying =
          engine.store.getState().isPlaying ||
          (engine.video?.paused === false);
        if (isActuallyPlaying) {
          return;
        }

        hls.stopLoad();
        engine.hlsLoadStarted = false;
      };

      const maybeStopPreload = (): void => {
        const hasPreviewFrame = (engine.video?.readyState ?? 0) >= 2;
        if (hasPreviewFrame || bufferedFragCount >= PREVIEW_PRELOAD_MAX_FRAGS) {
          stopPreload();
        }
      };

      hls.on(Hls.Events.FRAG_BUFFERED, onFragBuffered);
      engine.video?.addEventListener('loadeddata', onLoadedData, { once: true });
      safetyTimer = setTimeout(stopPreload, PREVIEW_SAFETY_TIMEOUT_MS);
    };

    const initialQuality = engine.store.getState().quality;
    const requestedHeight = parseInt(initialQuality, 10);
    const selectedIdx = initialQuality !== 'auto' && !isNaN(requestedHeight)
      ? hls.levels.reduce((closestIdx, level, idx) => {
        if (!Number.isFinite(level.height)) return closestIdx;
        if (closestIdx === -1) return idx;
        const closestHeight = hls.levels[closestIdx].height;
        const distance = Math.abs(level.height - requestedHeight);
        const closestDistance = Math.abs(closestHeight - requestedHeight);
        return distance < closestDistance || (distance === closestDistance && level.height > closestHeight)
          ? idx
          : closestIdx;
      }, -1)
      : -1;

    if (selectedIdx !== -1) {
      const selectedQuality = String(hls.levels[selectedIdx].height);
      const deferQualityApplyUntilPlay = !shouldPlay && !engine.hlsLoadStarted;
      if (deferQualityApplyUntilPlay) {
        // Cold idle start (autoplay disabled): queue preferred level without
        // forcing continuous loading.
        //
        // We preload enough data for a visible paused frame,
        // then immediately stop network loading until user interaction.
        hls.nextLevel = selectedIdx;
        engine.store.setState({ quality: selectedQuality });
        preloadPausedPreview();
      } else {
        engine.setQuality(selectedQuality);
      }
    } else {
      hls.currentLevel = -1;
      engine.store.setState({ quality: 'auto' });

      const shouldPreloadPausedPreview = !shouldPlay && !engine.hlsLoadStarted;
      if (shouldPreloadPausedPreview) {
        preloadPausedPreview();
      }
    }

    engine.shouldResumeOnNextManifest = false;
    if (shouldPlay) {
      hls.startLoad();
      engine.hlsLoadStarted = true;
      engine.play();
    }

    engine.startSpeedMonitor();

    if (!engine.store.getState().isOnline) {
      engine.store.setState({ isOnline: true });
      engine.eventBus.emit('stream:online');
    }
  });

  hls.on(Hls.Events.LEVEL_SWITCHING, (_event: unknown, data: any) => {
    const fromLevel = hls.levels[hls.currentLevel];
    const toLevel = hls.levels[data.level];
    if (fromLevel === undefined || toLevel === undefined) return;

    engine.levelSwitchStartedAt = Date.now();
    engine.fragLoadErrorCount = 0;
    engine.isLevelSwitching = true;
    const storeQuality = engine.store.getState().quality;
    const targetHeight = toLevel?.height ?? '?';
    const isUserSwitch = String(targetHeight) === storeQuality;

    if (storeQuality !== 'auto' && !isUserSwitch) {
      const now = Date.now();
      if (engine.lastQualityReassertAt === 0) {
        engine.lastQualityReassertAt = now;
      }
      if (now - engine.lastQualityReassertAt >= QUALITY_REASSERT_HOLD_MS) {
        engine.logger.warn(
          `[quality:lock] manual lock ${storeQuality}p could not be restored within ${QUALITY_REASSERT_HOLD_MS}ms — falling back to ABR auto`,
        );
        engine.pendingQualityReassert = false;
        engine.lastQualityReassertAt = 0;
        engine.setQuality('auto');
        return;
      }

      const desiredHeight = parseInt(storeQuality, 10);
      if (!isNaN(desiredHeight)) {
        const desiredIdx = hls.levels.findIndex(l => l.height === desiredHeight);
        if (desiredIdx !== -1 && desiredIdx !== data.level) {
          engine.logger.warn(
            `[quality:lock] hls.js tried to auto-switch to ${targetHeight}p but user locked ${storeQuality}p — re-asserting index ${desiredIdx}`,
          );
          engine.pendingQualityReassert = true;
          engine.applyQualityLevel(desiredIdx);
          return;
        }
      }
    }

    engine.logger.info(
      `[quality:switch${isUserSwitch ? '' : ':internal'}] ${fromLevel?.height ?? '?'}p (idx ${hls.currentLevel}) → ${targetHeight}p (idx ${data.level})` +
      ` | buf=${engine.getBufferLength().toFixed(1)}s ct=${engine.video?.currentTime.toFixed(1) ?? '?'}s`,
    );
  });

  hls.on(Hls.Events.LEVEL_SWITCHED, (_event: unknown, data: any) => {
    const height = hls.levels[data.level]?.height;
    if (height !== undefined && previousLevelHeight !== undefined && height < previousLevelHeight && hls.autoLevelEnabled) {
      engine.eventBus.emit('playback:recovery', { action: 'quality_down', reason: 'abr_quality_decrease' });
    }
    previousLevelHeight = height;
    const elapsed = Date.now() - engine.levelSwitchStartedAt;
    engine.isLevelSwitching = false;
    const level = hls.levels[data.level];
    if (!level) return;

    const label = String(level.height);
    engine.logger.info(
      `[quality:switched] ${label}p (idx ${data.level}) in ${elapsed}ms` +
      ` | buf=${engine.getBufferLength().toFixed(1)}s bw=${engine.getBandwidth().toFixed(1)}Mbps`,
    );

    const { quality } = engine.store.getState();
    if (quality === 'auto') return;

    if (quality === label) {
      engine.pendingQualityReassert = false;
      engine.lastQualityReassertAt = 0;
      engine.store.setState({ quality: label });
      return;
    }

    if (engine.lastQualityReassertAt === 0) {
      engine.lastQualityReassertAt = Date.now();
    }

    if (
      engine.lastQualityReassertAt > 0 &&
      Date.now() - engine.lastQualityReassertAt >= QUALITY_REASSERT_HOLD_MS
    ) {
      engine.logger.warn(
        `[quality:lock] manual lock ${quality}p could not be restored within ${QUALITY_REASSERT_HOLD_MS}ms after latest attempt — falling back to ABR auto`,
      );
      engine.pendingQualityReassert = false;
      engine.lastQualityReassertAt = 0;
      engine.setQuality('auto');
      return;
    }

    if (!engine.pendingQualityReassert) {
      const requestedHeight = parseInt(quality, 10);
      if (!isNaN(requestedHeight)) {
        const targetIdx = hls.levels.findIndex(l => l.height === requestedHeight);
        if (targetIdx !== -1) {
          engine.logger.debug('[HLS]', `Level switched to ${label} but user wants ${quality} — re-locking to index ${targetIdx}`);
          engine.pendingQualityReassert = true;
          engine.applyQualityLevel(targetIdx);
        }
      }
    }
  });

  hls.on(Hls.Events.BUFFER_FLUSHING, (_event: unknown, data: any) => {
    engine.logger.debug('[HLS]',
      `[buffer:flushing] type=${data.type ?? 'all'} ` +
      `${data.startOffset.toFixed(1)}s–${isFinite(data.endOffset) ? data.endOffset.toFixed(1) + 's' : '∞'}`,
    );
  });

  hls.on(Hls.Events.BUFFER_FLUSHED, (_event: unknown, data: any) => {
    engine.logger.debug('[HLS]', `[buffer:flushed] type=${data.type}`);
  });

  hls.on(Hls.Events.LEVEL_LOADED, (_event: unknown, data: any) => {
    engine.eventBus.emit('hls:request', buildRequestMetrics('playlist', data));
    engine.eventBus.emit('hls:playlist:loaded');
    const d = data.details;
    // Only an explicit live=false marks VOD; a missing live flag means the
    // manifest said nothing — stay 'unknown' rather than guessing live and
    // prematurely hiding VOD controls (timeline, poster).
    engine.store.setState({ streamType: d.live === false ? 'vod' : d.live === true ? 'live' : 'unknown' });
    engine.logger.debug('[HLS]',
      `[level:loaded] idx=${data.level} sn=${d.startSN}–${d.endSN}` +
      ` (${d.fragments.length} frags, ${d.targetduration}s target, live=${String(d.live)})`,
    );
  });

  hls.on(Hls.Events.LEVEL_UPDATED, (_event: unknown, data: any) => {
    const d = data.details;
    if (engine.video) {
      if (
        engine.isLowLatency &&
        !engine.initialLiveLatencyAligned &&
        engine.store.getState().isPlaying
      ) {
        if (engine.initialLiveLatencySeeked) {
          // Seek done — keep the spinner up until the jump is buffered over,
          // so the reveal is not followed by an immediate stall.
          if (engine.hasInitialLiveLatencyRevealBuffer()) {
            engine.completeInitialLiveLatencyAlignment();
          }
        } else {
          const alignment = alignInitialLiveLatency(hls, engine.video, engine.logger);
          if (alignment === 'aligned') {
            // Already inside the target window — nothing to hide.
            engine.completeInitialLiveLatencyAlignment();
          } else if (alignment === 'corrected') {
            engine.initialLiveLatencySeeked = true;
            if (engine.hasInitialLiveLatencyRevealBuffer()) {
              engine.completeInitialLiveLatencyAlignment();
            }
          }
        }
      }

      engine.store.setState({ latency: hls.latency });
      // Latency control belongs to LL-HLS only. Standard HLS has no latency
      // target to hold, so it must not accumulate stall penalties or drive the
      // playback rate — buffer events below stay shared between both engines.
      if (engine.isLowLatency) {
        // The elevation decays on real playback time, whether or not latency
        // control is suspended: a stall must not be able to freeze its own
        // penalty in place by keeping the controller paused. readyState < 3 means
        // the picture is not actually moving, so that time is not credited.
        if (!engine.video.paused && engine.video.readyState >= 3) {
          engine.latencyTarget.noteHealthy();
        }
        if (engine.isLatencyPausedForBuffer) {
          engine.video.playbackRate = 1.0;
          engine.latencyCorrectionState = 'none';
        } else {
          const now = Date.now();
          if (now - engine.lastLatencyRateAt >= 500) {
            engine.lastLatencyRateAt = now;
            engine.latencyCorrectionState = applyLatencyControl(
              hls,
              engine.video,
              engine.logger,
              1,
              engine.latencyCorrectionState,
              engine.latencyTarget.get(),
            );
          }
        }
      }
    }
    engine.instabilityDetector?.check(
      hls.latency ?? 0,
      (engine.video?.paused ?? true) || engine.isLatencyPausedForBuffer,
    );
    engine.logger.debug('[HLS]',
      `[level:updated] idx=${data.level} available sn=${d.startSN}-${d.endSN}` +
      ` (${d.fragments.length} frags)`,
    );
    d.fragments.forEach((frag: any) => {
      engine.logger.debug('[HLS]', `  frag sn=${String(frag.sn)} start=${frag.start.toFixed(2)}s dur=${frag.duration.toFixed(2)}s url=${frag.url}`);
    });
  });

  hls.on(Hls.Events.FRAG_CHANGED, (_event: unknown, data: any) => {
    engine.logger.debug('[HLS]',
      `[frag:changed] level=${data.frag.level} sn=${String(data.frag.sn)} url=${data.frag.url}`,
    );
  });

  hls.on(Hls.Events.ERROR, (_event: unknown, data: any) => {
    engine.logger.warn(`HLS error: ${data.type} / ${data.details}`, data.fatal ? '(FATAL)' : '');

    const src = engine.lastSource?.master ?? engine.lastSource?.source;
    const quality = engine.store.getState().quality;
    const responseCode = engine.extractResponseCode(data);
    const requestKind = requestKindForError(data.details ?? '');
    if (requestKind) engine.eventBus.emit('hls:request', buildRequestMetrics(requestKind, { ...data, response: { code: responseCode } }, true));

    if (data.details === 'bufferStalledError') {
      if (engine.isLowLatency) {
        engine.latencyTarget.noteStall();
        const stallTarget = engine.latencyTarget.get();
        if (!engine.isLatencyPausedForBuffer && shouldSuspendForBuffer(hls.latency, stallTarget)) {
          engine.isLatencyPausedForBuffer = true;
          engine.logger.warn(
            '[latency:paused] buffer stall — suspending latency control until buffer recovers' +
              ` above ${bufferRecoveryThreshold(stallTarget).toFixed(1)}s`,
          );
        }
      }
      engine.eventBus.emit('buffer:stall', {
        currentTime: engine.video?.currentTime ?? 0,
        bufferLength: engine.getBufferLength(),
        src,
        quality,
      });
      return;
    }

    if (
      data.details === 'bufferSeekOverHole' ||
      data.details === 'bufferNudgeOnStall'
    ) {
      engine.logger.debug('[HLS]', `${data.details} — self-healing`);
      return;
    }

    if (data.details === 'fragGap') {
      engine.fragGapErrorCount++;
      engine.logger.debug('[HLS]', `fragGap — self-healing (${engine.fragGapErrorCount}/${FRAG_GAP_ERROR_THRESHOLD})`);
      if (engine.fragGapErrorCount >= FRAG_GAP_ERROR_THRESHOLD) {
        engine.fragGapErrorCount = 0;
        engine.logger.warn('[HLS] fragGap threshold reached — falling back to ABR auto');
      }
      return;
    }

    if (!data.fatal) {
      if (data.details === 'bufferAppendError') {
        engine.eventBus.emit('buffer:append:error', { start: 0, end: 0, error: data });
      }

      if (data.details === 'fragLoadError') {
        const fragInfo = data.frag
          ? `sn=${String(data.frag.sn)} level=${data.frag.level} start=${data.frag.start.toFixed(2)}s`
          : 'frag=unknown';
        const isAudioFrag = data.frag?.type === 'audio';
        if (engine.isLevelSwitching) {
          engine.logger.debug('[HLS]',
            `[frag:loadError] ${fragInfo} http=${responseCode ?? '?'} [during-switch, not counted]`,
          );
          if (engine.isLowLatency) {
            engine.onFragLoadErrorDuringSwitch(data.frag?.start ?? 0);
          }
        } else if (isAudioFrag) {
          engine.logger.debug('[HLS]',
            `[frag:loadError] ${fragInfo} http=${responseCode ?? '?'} [audio-track, not counted]`,
          );
        } else {
          engine.logger.warn(
            `[frag:loadError] ${fragInfo} http=${responseCode ?? '?'}` +
            ` streak=${engine.fragLoadErrorCount + 1}/${FRAG_LOAD_ERROR_THRESHOLD}`,
          );
          engine.fragLoadErrorCount++;
          if (engine.fragLoadErrorCount >= FRAG_LOAD_ERROR_THRESHOLD) {
            engine.fragLoadErrorCount = 0;
            if (engine.isLowLatency) {
              engine.onFragLoadErrorThreshold();
            }
          }
        }
      }

      engine.eventBus.emit('hls:error', {
        type: data.type,
        details: data.details,
        fatal: false,
        src,
        quality,
        responseCode,
      });
      return;
    }

    if (engine.isAccessDeniedError(data, responseCode)) {
      engine.logger.error('Access denied (403) — stopping retries');
      engine.store.setState({ accessDenied: false, loading: false });
      engine.eventBus.emit('error', {
        code: 403,
        message: 'Access denied',
        fatal: true,
        details: data,
      });
      engine.stop();
      const pending = engine.store.getState().tokenRecoveryPending;
      engine.store.setState({ accessDenied: !pending, loading: pending });
      return;
    }

    // Classify offline before analytics observes the fatal error.
    const is404 = data.type === Hls.ErrorTypes.NETWORK_ERROR && responseCode === 404;
    if (is404) {
      engine.store.setState({ isOnline: false, isPlaying: false });
      engine.eventBus.emit('stream:offline');
    }

    engine.eventBus.emit('hls:error', {
      type: data.type,
      details: data.details,
      fatal: true,
      src,
      quality,
      responseCode,
    });

    const timeout = is404 ? 5000 : 30000;

    engine.logger.error(`Fatal ${data.type} error, restarting in ${timeout}ms (attempt ${engine.restartCount})...`);
    engine.eventBus.emit('error', {
      code: responseCode ?? 500,
      message: data.details,
      fatal: true,
      details: data,
    });

    engine.scheduleRestart(timeout);
  });

  hls.on(Hls.Events.FRAG_LOADING, (_event: unknown, data: any) => {
    engine.logger.debug('[HLS]',
      `[frag:loading] sn=${String(data?.frag?.sn ?? '?')} level=${data?.frag?.level ?? '?'}` +
      ` start=${data?.frag?.start.toFixed(2) ?? '?'}s dur=${data?.frag?.duration.toFixed(2) ?? '?'}s` +
      (engine.isLevelSwitching ? ' [during-switch]' : ''),
    );
    if (!engine.store.getState().isPlaying || engine.isLevelSwitching) return;
    const bufferLength = engine.getBufferLength();
    if (bufferLength < BUFFER_CRITICAL_THRESHOLD) {
      if (engine.isLowLatency) {
        engine.latencyTarget.noteStall();
        if (
          !engine.isLatencyPausedForBuffer &&
          shouldSuspendForBuffer(hls.latency, engine.latencyTarget.get())
        ) {
          engine.isLatencyPausedForBuffer = true;
          engine.logger.warn(`[latency:paused] buffer critical (${bufferLength.toFixed(1)}s) — suspending latency control immediately`);
        }
      }
      engine.eventBus.emit('buffer:critical', {
        bufferLength,
        currentTime: engine.video?.currentTime ?? 0,
        src: engine.lastSource?.master ?? engine.lastSource?.source,
        quality: engine.store.getState().quality,
      });
    }
  });

  hls.on(Hls.Events.FRAG_LOADED, (_event: unknown, data: any) => {
    engine.eventBus.emit('hls:request', buildRequestMetrics('fragment', data));
    // Audio can be served elsewhere. Attribute video throughput to its own response.
    if (!data?.frag?.type || data.frag.type === 'main') {
      const server = readResponseServer(data?.networkDetails);
      if (data?.frag && typeof data.frag === 'object') fragmentServers.set(data.frag, server);
    }
    engine.logger.debug(
      `[frag:loaded] sn=${String(data?.frag?.sn ?? '?')} level=${data?.frag?.level ?? '?'}` +
      ` type=${data?.frag?.type ?? '?'} part=${data?.part?.index ?? '-'}`,
    );
  });

  hls.on(Hls.Events.FRAG_BUFFERED, (_event: unknown, data: any) => {
    const server = data?.frag && fragmentServers.get(data.frag);
    if (server) {
      const previous = engine.store.getState();
      if (previous.cdnServer !== server.name || previous.cdnServerIp !== server.ip) {
        // Do not label a speed report containing samples from multiple servers as IX.
        engine.speedDownloadSpeeds = [];
        engine.speedTotalBytes = 0;
        engine.speedTotalTime = 0;
        engine.lastFragSampleAt = 0;
      }
      engine.store.setState({ cdnServer: server.name, cdnServerIp: server.ip, cdnServerSource: 'fragment' });
    }
    engine.fragLoadErrorCount = 0;
    engine.fragGapErrorCount = 0;
    engine.logger.debug('[HLS]',
      `[frag:buffered] sn=${String(data?.frag?.sn ?? '?')} level=${data?.frag?.level ?? '?'}` +
      ` dur=${data?.frag?.duration.toFixed(2) ?? '?'}s buf=${engine.getBufferLength().toFixed(1)}s`,
    );

    const bufferLength = engine.getBufferLength();
    engine.store.setState({ bufferLength });
    engine.eventBus.emit('buffer:update', { length: bufferLength, target: 0 });

    // Check buffer recovery before emitting buffer:low — if buffer crossed the recovery
    // threshold this fragment, resume latency control immediately. LL-HLS only:
    // standard HLS never suspends latency control, so it has nothing to resume.
    const latencyTarget = engine.latencyTarget.get();
    const recoveryThreshold = Math.max(
      BUFFER_RECOVERY_THRESHOLD,
      bufferRecoveryThreshold(latencyTarget),
    );
    const bufferRecovered = bufferLength >= recoveryThreshold;
    // Suspension is pointless once latency is back at the target: see
    // shouldSuspendForBuffer. Resuming there hands the stream to the slow-down
    // branch, which is what rebuilds the buffer, instead of stranding it.
    const nothingToSuspend = !shouldSuspendForBuffer(hls.latency, latencyTarget);
    if (
      engine.isLowLatency &&
      engine.isLatencyPausedForBuffer &&
      (bufferRecovered || nothingToSuspend)
    ) {
      engine.isLatencyPausedForBuffer = false;
      // Only a real buffer recovery closes the stall episode. A resume granted
      // because latency sits low must not re-arm the penalty for the same stall.
      if (bufferRecovered) engine.latencyTarget.noteRecovered();
      engine.logger.warn(
        `[latency:resumed] buffer ${bufferLength.toFixed(1)}s` +
          (bufferRecovered
            ? ` recovered (need ${recoveryThreshold.toFixed(1)}s)`
            : ` with latency at the ${latencyTarget.toFixed(2)}s target`) +
          ' — resuming latency control',
      );
    }

    const stats = data?.stats;
    if (!stats) return;

    const timeToLoad = stats.loading.end - stats.loading.start;
    const bandwidth = stats.total > 0 && timeToLoad > 0
      ? Math.round((8 * stats.total) / (stats.buffering.end - stats.loading.start))
      : 0;
    engine.store.setState({ bandwidth });
    const src = engine.lastSource?.master ?? engine.lastSource?.source;
    const quality = engine.store.getState().quality;

    // The pause decision follows the adaptive target: once the target has been
    // raised by earlier stalls, a buffer that was "fine" at 2.7s is low again.
    const lowThreshold = Math.max(BUFFER_LOW_THRESHOLD, slowDownThreshold(latencyTarget));
    if (engine.isLowLatency && bufferLength < lowThreshold && !engine.isLatencyPausedForBuffer) {
      if (shouldSuspendForBuffer(hls.latency, latencyTarget)) {
        engine.isLatencyPausedForBuffer = true;
        engine.logger.warn(`[latency:paused] buffer low (${bufferLength.toFixed(1)}s) - suspending latency control until buffer recovers`);
      } else {
        engine.logger.debug(
          '[HLS]',
          `[latency:steady] buffer low (${bufferLength.toFixed(1)}s) with latency at the ${latencyTarget.toFixed(2)}s target - keeping latency control active`,
        );
      }
    }

    // Latency control intervenes early; telemetry reports a depleted buffer.
    // LL-HLS routinely dips below 2s while tracking its 2.7s live-latency target.
    const telemetryLowThreshold = engine.isLowLatency ? LL_BUFFER_LOW_THRESHOLD : BUFFER_LOW_THRESHOLD;
    if (bufferLength < telemetryLowThreshold) {
      engine.eventBus.emit('buffer:low', {
            bufferLength,
            bandwidth,
        targetDuration: data.frag.duration,
        timeToLoad,
        src,
        quality,
      });
    }

    const downloadTime = timeToLoad;
    const bytes = stats.loaded;
    const isMainFragment = !data.frag.type || data.frag.type === 'main';
    if (isMainFragment && downloadTime > 0 && bytes > 0) {
      const totals = accumulateSpeedSample(
        engine.speedDownloadSpeeds,
        engine.speedTotalBytes,
        engine.speedTotalTime,
        bytes,
        downloadTime,
      );
      engine.speedTotalBytes = totals.totalBytes;
      engine.speedTotalTime = totals.totalTimeMs;
    }

    const now = Date.now();
    if (isMainFragment && now - engine.lastFragSampleAt >= FRAG_SAMPLE_INTERVAL) {
      engine.lastFragSampleAt = now;
      engine.eventBus.emit('hls:frag:sample', {
        sn: data.frag.sn as number,
        level: data.frag.level,
        duration: data.frag.duration,
        size: stats.total,
        loadTime: timeToLoad,
        bandwidth,
        src,
        quality,
      });
    }
  });
}
